import { useState, useEffect, useCallback } from "react";
import { DollarSign, Plus, Trash2, Edit, Layers, Eye, X, Boxes, ClipboardList, History, BarChart3, Search, PackageOpen, Download, Gauge } from "lucide-react";
import { toast } from "sonner";
import SearchableSelect from "@/components/ui/SearchableSelect";
import api, { errMsg, money } from "../lib/api";
import { todayIST, fmt12, fmtDate } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";

// Reporting floor: the Comparison table never reaches back past June 2026,
// which is where the salon's month columns and its measuring period start.
const MONTH_FLOOR = "2026-09";

const monthKey = (d) => (d || "").slice(0, 7);

const addMonths = (k, n) => {
  let y = Number(k.slice(0, 4));
  let m = Number(k.slice(5, 7)) + n;
  y += Math.floor((m - 1) / 12);
  m = (((m - 1) % 12) + 12) % 12 + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
};

const monthLabel = (k) => {
  const [y, m] = k.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-GB", { month: "short", year: "numeric" });
};

const monthStart = (k) => `${k}-01`;

const monthEnd = (k) => {
  const [y, m] = k.split("-").map(Number);
  return `${k}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
};

// Same adherence the Comparison measures: budget ÷ actual as a percentage.
// 100 is exactly the planned amount and every point below it is an overrun,
// so green means AT OR UNDER budget (>= 100), amber up to ~11% over, red
// beyond that — one rule, matching `_adh_status` on the backend.
const adherence = (b, a) => {
  const bb = Number(b || 0), aa = Number(a || 0);
  if (aa <= 0) return 100.0;
  return Math.round((bb / aa) * 1000) / 10;
};
const adhStatus = (e) => (e >= 100 ? "good" : e >= 90 ? "watch" : "leak");

/**
 * One month range, used by BOTH the Comparison and the POS service window.
 *
 * They have to share it: the average on Comparison is actual ÷ billed services
 * over the same period, so the two screens only tie out when they are looking
 * at the identical span of months.
 */
const MonthRangePicker = ({ from, to, allMonths, onChange }) => {
  const cls = "px-3 py-2 text-xs font-extrabold uppercase tracking-wider rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 focus:border-amber-500 outline-none";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[11px] font-bold text-slate-400 uppercase">From</span>
      <select value={from} onChange={(e) => { const v = e.target.value; onChange(v, v > to ? v : to); }} className={cls}>
        {allMonths.filter((k) => k <= to).map((k) => <option key={k} value={k}>{monthLabel(k)}</option>)}
      </select>
      <span className="text-slate-400 text-sm font-black">→</span>
      <span className="text-[11px] font-bold text-slate-400 uppercase">To</span>
      <select value={to} onChange={(e) => { const v = e.target.value; onChange(v < from ? v : from, v); }} className={cls}>
        {allMonths.filter((k) => k >= from).map((k) => <option key={k} value={k}>{monthLabel(k)}</option>)}
      </select>
      <span className="text-[11px] font-bold text-slate-400 uppercase ml-1">auto-refreshes live</span>
    </div>
  );
};

const SectionSearch = ({ value, onChange, placeholder }) => (  <div className="relative mb-4">
    <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="lss-input lss-search w-full py-2.5 text-sm font-semibold"
      style={{ paddingLeft: "2.75rem", paddingRight: "2.75rem" }}
    />
    {(value || "") && (
      <button
        onClick={() => onChange("")}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
      >
        <X className="w-4 h-4" />
      </button>
    )}
  </div>
);

const LeakTable = ({ rows, q, nameKey, extra }) => {
  const list = (rows || []).filter((x) => {
    if (!q) return true;
    const n = ((x[nameKey] || "") + " " + (x.unit || "")).toLowerCase();
    return n.includes(q);
  });
  const badge = (st) => st === "good"
    ? "bg-emerald-100 text-emerald-900 border-emerald-300"
    : st === "watch"
      ? "bg-amber-100 text-amber-950 border-amber-300"
      : "bg-rose-100 text-rose-900 border-rose-300";
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg">
      <table className="lss-table">
        <thead>
          <tr>
            <th>Name</th>
            <th className="text-right">Budget Vol</th>
            <th className="text-right">Actual Vol</th>
            <th className="text-right">Budget ₹</th>
            <th className="text-right">Actual ₹</th>
            <th className="text-right">Efficiency</th>
            <th className="text-right">Variance ₹</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {list.map((x, i) => (
            <tr key={i} className="hover:bg-slate-50">
              <td className="text-xs font-bold text-slate-900">{x[nameKey]} {extra ? <span className="text-slate-400 font-semibold">· {extra(x)}</span> : null}</td>
              <td className="text-right tabular text-xs">{Number(x.budgeted_vol).toLocaleString()}</td>
              <td className="text-right tabular text-xs">{Number(x.actual_vol).toLocaleString()}</td>
              <td className="text-right tabular text-xs">₹{Number(x.budgeted_cost).toLocaleString()}</td>
              <td className="text-right tabular text-xs">₹{Number(x.actual_cost).toLocaleString()}</td>
              <td className="text-right tabular font-extrabold text-sm">
                <div className="flex items-center justify-end gap-2">
                  <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className={`h-full ${x.status === "good" ? "bg-emerald-500" : x.status === "watch" ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${Math.min(100, x.efficiency)}%` }} />
                  </div>
                  {x.efficiency}%
                </div>
              </td>
              <td className={`text-right tabular text-xs font-bold ${Number(x.variance) < 0 ? "text-rose-700" : "text-emerald-700"}`}>₹{Number(x.variance).toLocaleString()}</td>
              <td><span className={`lss-badge text-[10px] border font-bold uppercase ${badge(x.status)}`}>{x.status}</span></td>
            </tr>
          ))}
          {list.length === 0 && (
            <tr><td colSpan={8} className="text-center py-6 text-slate-500 text-xs font-semibold">No rows in range — commit services or stock out first.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const WHOLE_UNITS = new Set(["bottle", "bottles", "box", "boxes", "jar", "jars", "tube", "tubes", "can", "cans", "set", "sets", "strip", "strips", "tab", "tabs", "pkt"]);
const MEASURED_UNITS = new Set(["ml", "l", "ltr", "litre", "gm", "grm", "g", "kg"]);

const bottlePriceOf = (s) => Number(s?.bottle_price ?? s?.unit_price ?? s?.unit_cost ?? 0) || 0;
const packSizeOf = (s) => {
  let pack = Number(s?.pack_size ?? s?.qty ?? 0) || 0;
  const u = (s?.unit || "").toLowerCase();
  if (u === "l" || u === "ltr" || u === "litre" || u === "kg") pack *= 1000;
  return pack;
};

const isWholeUnit = (unit) => {
  const u = (unit || "").trim().toLowerCase();
  return WHOLE_UNITS.has(u);
};

// ALWAYS per-measure rate for ml/gm/pcs (bottle price / pack qty). Full price
// only when opening a whole sealed container (bottle/box/jar/...).
const rateFor = (sku, unit) => {
  if (!sku) return 0;
  const bottle = bottlePriceOf(sku);
  if (isWholeUnit(unit)) return bottle;
  const pack = packSizeOf(sku);
  return bottle / (pack > 0 ? pack : 100);
};

const skuRateLabel = (s) => {
  const bottle = bottlePriceOf(s);
  const pack = packSizeOf(s);
  const rate = rateFor(s, "ml");
  const packTxt = pack > 0 ? `${pack}${s.unit ? ` ${s.unit}` : ""}` : "pack ?";
  return `${s.name} — ₹${rate.toFixed(2)}/ml (bottle ₹${bottle.toLocaleString()} / ${packTxt})`;
};

/**
 * The base measure an audit-derived figure is reported in.
 *
 * A 1 litre pack is counted in ml and a 1 kg pack in gm, because
 * `_pack_volume` on the server normalises exactly that way — so the label has
 * to follow the conversion or "1500 ml" would read as "1500 l".
 */
const baseUnit = (u) => {
  const s = String(u || "").trim().toLowerCase();
  if (["l", "ltr", "litre", "liter", "ml"].includes(s)) return "ml";
  if (["kg", "kgs", "g", "gm", "grm", "gram", "grams"].includes(s)) return "gm";
  return String(u || "unit");
};

// What a budget can sensibly be expressed in. Stock is planned in small
// containers ("2 boxes") as often as in millilitres, and the two are not
// interchangeable, so the editor asks which one you mean.
const BUDGET_UNITS = ["ml", "gm", "pcs", "piece", "bottle", "box", "tube", "jar",
                      "can", "pack", "strip", "sheet", "sachet", "set", "l", "kg"];

// Multi-select for the manual product -> POS service mapping: chips show the
// combination currently mapped, the input adds from billed service names,
// x drops one. Every change saves the whole set at once.
const PosServiceMapCell = ({ rowKey, selected, options, onSave }) => {
  const [val, setVal] = useState("");
  const [open, setOpen] = useState(false);
  const q = val.trim().toLowerCase();
  const matches = (options || []).filter(
    (n) => (n || "").toLowerCase().includes(q) && !(selected || []).includes(n)
  );
  const add = (name) => {
    const v = (name || "").trim();
    if (!v) return;
    const next = [...(selected || [])];
    if (!next.includes(v)) next.push(v);
    setVal("");
    setOpen(false);
    onSave(next);
  };
  return (
    <div className="min-w-[150px] max-w-[230px]">
      <div className="flex flex-wrap gap-1 mb-1">
        {(selected || []).map((n) => (
          <span key={n} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-indigo-50 text-indigo-900 border border-indigo-200">
            {n}
            <button
              type="button"
              title="Remove"
              onClick={() => onSave((selected || []).filter((x) => x !== n))}
              className="text-indigo-500 hover:text-rose-700 font-black leading-none"
            >
              ×
            </button>
          </span>
        ))}
        {(selected || []).length === 0 && (
          <span className="text-[10px] font-semibold text-slate-400">No service mapped</span>
        )}
      </div>
      <div className="relative">
        <input
          value={val}
          onChange={(e) => { setVal(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="+ Add service…"
          title="Which POS services this product is used in (display only)"
          className="w-full text-xs px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-800 font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 placeholder-slate-400"
          autoComplete="off"
        />
        {open && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
            <div className="py-1 max-h-48 overflow-y-auto">
              {matches.slice(0, 30).map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); add(n); }}
                  className="w-full text-left px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-amber-50 hover:text-amber-900 transition"
                >
                  {n}
                </button>
              ))}
              {val.trim() && !matches.includes(val.trim()) && (
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); add(val); }}
                  className="w-full text-left px-3 py-1.5 text-xs font-bold text-emerald-800 hover:bg-emerald-50 transition"
                >
                  + Use “{val.trim()}”
                </button>
              )}
              {matches.length === 0 && !val.trim() && (
                <div className="px-3 py-2 text-xs text-slate-400 font-semibold text-center">Type to search billed services</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function COGSPage() {
  const [activeSection, setActiveSection] = useState("dashboard"); // dashboard | leakage | opened | recipes | workbench | history | analysis
  const [recipes, setRecipes] = useState([]);
  const [analysis, setAnalysis] = useState([]);
  const [skus, setSkus] = useState([]);
  const [effData, setEffData] = useState(null);
  const [effDays, setEffDays] = useState(30);
  const [cmpData, setCmpData] = useState(null);
  const [svcData, setSvcData] = useState(null);
  // The month range is shared by Comparison and the POS service window, so the
  // average on one screen is computed over exactly the services listed on the
  // other. The window opens at the September reporting floor (owner, Oct 2026).
  const currentMonth = (() => { const c = monthKey(todayIST()); return c > MONTH_FLOOR ? c : MONTH_FLOOR; })();
  const allMonths = (() => {
    const out = [];
    let k = MONTH_FLOOR;
    while (k <= currentMonth) { out.push(k); k = addMonths(k, 1); }
    return out;
  })();
  const [rangeFrom, setRangeFrom] = useState(MONTH_FLOOR);
  const [rangeTo, setRangeTo] = useState(currentMonth);
  // The budget is ONE number per product, identical in every month, set once
  // from the ✎ button beside the product name. Everything else on this screen
  // is calculated, so there is nothing else to type into.
  const [budgetEdit, setBudgetEdit] = useState(null);
  const [budgetValue, setBudgetValue] = useState("");
  const [budgetUnit, setBudgetUnit] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);
  const [pools, setPools] = useState([]);
  const [showPoolModal, setShowPoolModal] = useState(false);
  const [poolName, setPoolName] = useState("");
  const [poolKeys, setPoolKeys] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [committed, setCommitted] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [serviceName, setServiceName] = useState("");
  const [category, setCategory] = useState("");
  const [ingredients, setIngredients] = useState([{ mode: "sku", sku_id: "", category_pool: "", quantity_per_service: 1, unit: "ml" }]);
  const [pageLoading, setPageLoading] = useState(true);
  const [commitModalDraft, setCommitModalDraft] = useState(null);
  const [commitIngs, setCommitIngs] = useState([]);
  const [stockHistoryCogs, setStockHistoryCogs] = useState(null);
  const [searches, setSearches] = useState({ recipes: "", workbench: "", history: "", analysis: "", comparison: "", services: "" });
  const setSearch = (sec, val) => setSearches((prev) => ({ ...prev, [sec]: val }));
  const qFor = (sec) => (searches[sec] || "").trim().toLowerCase();

  useEffect(() => {
    Promise.all([fetchRecipes(), fetchAnalysis(), fetchSkus(), fetchDrafts(), fetchCommitted(), fetchPools(), fetchSvc(), fetchCmp()]).finally(() => setPageLoading(false));
  }, []);

  const fetchEff = async (days) => {
    try {
      const res = await api.get(`/cogs/unified-efficiency?days=${days || effDays}`);
      setEffData(res.data);
    } catch (e) { console.error(e); }
  };

  // Comparison is read over the selected month range instead of a fixed
  // "last N months" lookback, so the salon picks exactly which span it wants.
  const fetchCmp = async (from, to) => {
    const s = from || rangeFrom;
    const e = to || rangeTo;
    try {
      const res = await api.get(`/cogs/monthly-comparison?start_month=${s}&end_month=${e}`);
      // This runs on a 30s poll. Keep the previous object identity when the
      // payload is unchanged so React skips the re-render outright — otherwise
      // every poll needlessly redraws the comparison tables and charts in view.
      setCmpData((prev) => (JSON.stringify(prev) === JSON.stringify(res.data) ? prev : res.data));
    } catch (e) { console.error(e); }
  };

  // The POS service window reads the SAME span, which is what keeps the billed
  // counts here in step with "POS services in period" on Comparison — they are
  // one another's denominator, so they must be measured over identical months.
  const fetchSvc = async (from, to) => {
    const s = from || rangeFrom;
    const e = to || rangeTo;
    try {
      const res = await api.get(`/cogs/unified-efficiency?start_date=${monthStart(s)}&end_date=${monthEnd(e)}`);
      setSvcData((prev) => (JSON.stringify(prev) === JSON.stringify(res.data) ? prev : res.data));
    } catch (e) { console.error(e); }
  };

  const fetchStockHistory = useCallback(async (from, to) => {
    const s = from || rangeFrom;
    const e = to || rangeTo;
    const sDate = `${s}-01`;
    const [ey, em] = e.split("-").map(Number);
    const lastDay = new Date(ey, em, 0).getDate();
    const eDate = `${e}-${String(lastDay).padStart(2, "0")}`;

    if (eDate < "2026-08-21") {
      setStockHistoryCogs({
        opening: 0,
        inward: 0,
        outward: 0,
        closing: 0,
        consumption: 0,
        cogs: 0,
        skus_count: 0,
        startDate: sDate,
        endDate: eDate,
      });
      return;
    }
    const effFrom = sDate < "2026-08-21" ? "2026-08-21" : sDate;
    try {
      const res = await api.get(`/inventory/history?date_from=${effFrom}&date_to=${eDate}`);
      const summary = res.data?.summary;
      const rows = res.data?.rows || [];
      const op = summary?.opening_value != null ? Number(summary.opening_value) : rows.reduce((acc, r) => acc + (r.opening_value !== undefined ? (Number(r.opening_value) || 0) : ((Number(r.opening) || 0) * (Number(r.unit_price) || 0))), 0);
      const inc = summary?.in_value != null ? Number(summary.in_value) : rows.reduce((acc, r) => acc + (r.in_value !== undefined ? (Number(r.in_value) || 0) : ((Number(r.in) || 0) * (Number(r.unit_price) || 0))), 0);
      const out = rows.reduce((acc, r) => acc + (Number(r.out) || 0) * (Number(r.unit_price) || 0), 0);
      const cl = summary?.value != null ? Number(summary.value) : rows.reduce((acc, r) => acc + (Number(r.value) || 0), 0);
      const cons = summary?.consumption_value != null ? Number(summary.consumption_value) : rows.reduce((acc, r) => acc + (Number(r.consumption_value) || 0), 0);
      const cogsVal = summary?.cogs_value != null ? Number(summary.cogs_value) : (op + inc - cl);
      setStockHistoryCogs({
        opening: Math.round(op),
        inward: Math.round(inc),
        pi_inward: Math.round(summary?.pi_in_value != null ? Number(summary.pi_in_value) : inc),
        outward: Math.round(out),
        closing: Math.round(cl),
        consumption: Math.round(cons),
        cogs: Math.round(cogsVal),
        skus_count: rows.length || summary?.products || 456,
        startDate: effFrom,
        endDate: eDate,
        fromMonth: s,
        toMonth: e,
      });
    } catch (err) {
      console.error("Failed to fetch stock history for COGS window:", err);
    }
  }, [rangeFrom, rangeTo]);

  const openBudgetEditor = (r) => {
    const firstMonth = ((cmpData || {}).months || [])[0];
    const fallback = firstMonth ? Number(((r.months || {})[firstMonth.key] || {}).bv || 0) : 0;
    setBudgetEdit({ sku_id: r.key, name: r.name, unit: r.unit || "", kind: r.kind });
    setBudgetValue(String(r.budget_flat != null ? r.budget_flat : fallback));
    // The unit is part of the budget, so it is offered back for editing too —
    // "2 boxes" and "2 ml" are different budgets.
    setBudgetUnit(r.budget_unit || r.unit || "");
  };

  // One number, stored against "*" so it applies to every month in the window.
  const saveFlatBudget = async () => {
    if (!budgetEdit) return;
    if (budgetEdit.kind !== "sku") {
      toast.error("Category pools keep their recipe budget — edit the product itself.");
      return;
    }
    const n = parseFloat(budgetValue);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Budget must be 0 or more");
      return;
    }
    setSavingBudget(true);
    try {
      await api.post("/cogs/budget-override", { month: "*", sku_id: budgetEdit.sku_id, budget_qty: n, unit: budgetUnit });
      toast.success(`Budget set — ${budgetEdit.name}: ${n} ${budgetUnit || budgetEdit.unit} every month`);
      setBudgetEdit(null);
      fetchCmp();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingBudget(false);
    }
  };

  // Hand the budget back to the recipe default.
  const resetFlatBudget = async () => {
    if (!budgetEdit) return;
    setSavingBudget(true);
    try {
      await api.post("/cogs/budget-override", { month: "*", sku_id: budgetEdit.sku_id, clear: true });
      toast.success(`Recipe budget restored — ${budgetEdit.name}`);
      setBudgetEdit(null);
      fetchCmp();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingBudget(false);
    }
  };

  // The Budget column reports ONE month, because that is the granularity the
  // editor writes at: a figure of 5 is "5 per month", and the window showing
  // Sep–Oct must not display 10 — that made a successful save look like it had
  // been ignored or doubled. `total_bv` is still the range sum and is what the
  // backend prices the range with; only what is SHOWN here is per month.
  // For a recipe default (no override) the budget is PER-MONTH and UNEVEN --
  // it tracks services billed each month, so Sep can be 49 and Oct 0. Dividing
  // the range sum by the month count invented a fake "24.5" that matched no
  // real month. Show the leading month's actual budget instead (CUTTING SHEET:
  // 49, not 49/2).
  const monthListForBudget = ((cmpData || {}).months || []);
  const firstMonthKey = monthListForBudget.length ? monthListForBudget[0].key : null;
  const perMonthBudget = (r) => {
    if (r.budget_flat != null) return Number(r.budget_flat);
    if (firstMonthKey && r.months && r.months[firstMonthKey] != null) return Number(r.months[firstMonthKey].bv || 0);
    return Number(r.total_bv || 0);
  };
  // The Out column counts stock issued across the WHOLE displayed window, so
  // the range line has to quote that window. `period.end` is the audit date —
  // it only says when `left` was counted, and printing it as the end of the
  // range made the header contradict the column beneath it.
  const monthList = (cmpData || {}).months || [];
  const windowEnd = monthList.length
    ? monthList[monthList.length - 1].end
    : ((cmpData || {}).period || {}).end;
  const fetchPools = async () => {
    try { const res = await api.get("/cogs/category-pools"); setPools(res.data || []); } catch (e) { console.error(e); }
  };
  const handleSavePool = async (e) => {
    e.preventDefault();
    if (!poolName.trim()) return toast.error("Pool name required");
    try {
      await api.post("/cogs/category-pools", { name: poolName.trim(), keywords: poolKeys });
      toast.success("Pool saved!");
      setShowPoolModal(false);
      setPoolName(""); setPoolKeys("");
      fetchPools();
      fetchEff(effDays);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // Both month-range windows refetch whenever the range or the screen changes,
  // then keep themselves current while visible — no refresh button anywhere.
  // Dashboard reads the Comparison window; Analysis reads it plus POS services.
  useEffect(() => {
    if (activeSection === "comparison" || activeSection === "dashboard") {
      fetchCmp();
      fetchStockHistory();
    }
    if (activeSection === "services" || activeSection === "analysis") fetchSvc();
    if (activeSection === "analysis") fetchCmp();
  }, [activeSection, rangeFrom, rangeTo, fetchStockHistory]);

  useAutoRefresh(() => {
    if (activeSection === "comparison" || activeSection === "dashboard") {
      fetchCmp();
      fetchStockHistory();
    }
    else if (activeSection === "services" || activeSection === "analysis") { fetchSvc(); fetchCmp(); }
  });

  const fetchRecipes = async () => {
    try { const res = await api.get("/cogs/recipes"); setRecipes(res.data || []); } catch (e) { console.error(e); }
  };
  const fetchAnalysis = async () => {
    try { const res = await api.get("/cogs/analysis"); setAnalysis(res.data?.analysis || []); } catch (e) { console.error(e); }
  };
  const fetchSkus = async () => {
    try { const res = await api.get("/inventory/skus"); setSkus(res.data || []); } catch (e) { console.error(e); }
  };

  const fetchDrafts = async () => {
    try { const res = await api.get("/cogs/drafts?status=pending"); setDrafts(res.data || []); } catch (e) { console.error(e); }
  };
  const fetchCommitted = async () => {
    try { const res = await api.get("/cogs/committed"); setCommitted(res.data?.logs || []); } catch (e) { console.error(e); }
  };

  const addIngredientRow = () => {
    setIngredients([...ingredients, { mode: "sku", sku_id: "", category_pool: "", quantity_per_service: 1, unit: "ml" }]);
  };

  const removeIngredientRow = (idx) => {
    setIngredients(ingredients.filter((_, i) => i !== idx));
  };

  const openCreate = () => {
    setEditing(null);
    setServiceName("");
    setCategory("");
    setIngredients([{ mode: "sku", sku_id: "", category_pool: "", quantity_per_service: 1, unit: "ml" }]);
    setShowModal(true);
  };

  const openEdit = (recipe) => {    setEditing(recipe);
    setServiceName(recipe.service_name || "");
    setCategory(recipe.category || "");
    setIngredients(
      (recipe.ingredients || [{ mode: "sku", sku_id: "", category_pool: "", quantity_per_service: 1, unit: "ml" }]).map((ing) => ({
        mode: ing.category_pool ? "pool" : "sku",
        sku_id: ing.sku_id || "",
        category_pool: ing.category_pool || "",
        quantity_per_service: ing.quantity_per_service ?? 1,
        unit: ing.unit || "ml",
      }))
    );
    setShowModal(true);
  };

  const handleSaveRecipe = async (e) => {
    e.preventDefault();
    if (!serviceName.trim()) {
      toast.error("Please enter service name");
      return;
    }
    const validIngredients = ingredients
      .filter((i) => (i.mode === "pool" ? i.category_pool : i.sku_id))
      .map((i) => (i.mode === "pool"
        ? { category_pool: i.category_pool, quantity_per_service: Number(i.quantity_per_service) || 0, unit: i.unit }
        : { sku_id: i.sku_id, quantity_per_service: Number(i.quantity_per_service) || 0, unit: i.unit }));
    if (validIngredients.length === 0) {
      toast.error("Add at least one SKU or pool ingredient");
      return;
    }
    try {
      const res = await api.post("/cogs/recipes", {
        service_name: serviceName,
        category,
        ingredients: validIngredients,
      });
      toast.success(editing ? "Recipe updated!" : "Saved service recipe!");
      setShowModal(false);
      setEditing(null);
      setServiceName("");
      setCategory("");
      setIngredients([{ mode: "sku", sku_id: "", category_pool: "", quantity_per_service: 1, unit: "ml" }]);
      fetchRecipes();
      fetchAnalysis();
    } catch (err) {
      toast.error("Error saving recipe");
    }
  };

  const handleDeleteRecipe = async (recipe) => {
    if (!window.confirm(`Delete recipe '${recipe.service_name}' and its ingredients? Committed history is kept.`)) return;
    try {
      await api.delete(`/cogs/recipes/${recipe.id}`);
      toast.success(`Recipe '${recipe.service_name}' deleted.`);
      fetchRecipes();
      fetchAnalysis();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const normName = (s) =>
    (s || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\b(men|women|kids|boy|girl|male|female)\b/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const findRecipeForService = (serviceName) => {
    const target = normName(serviceName);
    if (!target) return null;
    let best = null;
    let bestCommon = 0;
    for (const r of recipes) {
      const rn = normName(r.service_name);
      if (!rn) continue;
      if (rn === target) return r;
      const tTokens = target.split(" ");
      const rTokens = rn.split(" ");
      const common = tTokens.filter((t) => rTokens.includes(t) && t.length > 1).length;
      const total = Math.max(tTokens.length, rTokens.length);
      const ratio = total ? common / total : 0;
      if (ratio >= 0.6 && common > bestCommon) {
        bestCommon = common;
        best = r;
      }
    }
    return best;
  };

  const openCommitModal = (draft) => {
    let base = (draft.ingredients || []).map((i) => ({
      sku_id: i.sku_id,
      sku_name: i.sku_name,
      qty: i.qty ?? 1,
      unit: i.unit || "ml",
    }));

    if (base.length === 0) {
      const recipe = findRecipeForService(draft.service_name);
      if (recipe) {
        base = (recipe.ingredients || [])
          .filter((ing) => ing.sku_id)
          .map((ing) => ({
            sku_id: ing.sku_id,
            sku_name: ing.sku_name || "",
            qty: ing.quantity_per_service ?? 1,
            unit: ing.unit || "ml",
          }));
        toast.success(`Recipe "${recipe.service_name}" ingredients auto-added`);
      }
    }

    setCommitModalDraft(draft);
    setCommitIngs(base.length ? base : [{ sku_id: "", sku_name: "", qty: 1, unit: "ml" }]);
  };

  const updateCommitIng = (idx, patch) => {
    const copy = [...commitIngs];
    copy[idx] = { ...copy[idx], ...patch };
    setCommitIngs(copy);
  };

  const addCommitIng = () => {
    setCommitIngs([...commitIngs, { sku_id: "", sku_name: "", qty: 1, unit: "ml" }]);
  };

  const removeCommitIng = (idx) => {
    setCommitIngs(commitIngs.filter((_, i) => i !== idx));
  };

  const handleCommit = async () => {
    if (!commitModalDraft) return;
    const valid = commitIngs.filter((i) => i.sku_id);
    if (valid.length === 0) {
      toast.error("Add at least one ingredient");
      return;
    }
    // Owner rule (20 Oct 2026): one stock — consumption just takes its share
    // off that number, so only "is there any stock at all" has to be checked.
    for (const i of valid) {
      const s = skus.find((x) => x.id === i.sku_id);
      const need = Number(i.qty) || 0;
      if (need > 0 && Number(s?.store_qty || 0) <= 0) {
        toast.error(`No stock left for '${s?.name || i.sku_name || "item"}'. Purchase it in first.`);
        return;
      }
    }
    try {
      await api.post("/cogs/commit-draft", {
        draft_id: commitModalDraft.id,
        ingredients: valid.map((i) => ({
          sku_id: i.sku_id,
          qty: Number(i.qty) || 0,
          unit: i.unit,
        })),
      });
      toast.success("Consumption committed & stock deducted!");
      setCommitModalDraft(null);
      fetchDrafts();
      fetchCommitted();
      fetchAnalysis();
    } catch (e) {
      toast.error(errMsg(e) || "Failed to commit consumption");
    }
  };

  if (pageLoading) return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8 animate-pulse">
      <div className="h-10 bg-slate-200 rounded w-1/3 mb-4"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-40 bg-slate-200 rounded-xl"></div>)}
      </div>
      <div className="h-96 bg-slate-200 rounded-xl w-full"></div>
    </div>
  );

  const filteredRecipes = (() => {
    const q = qFor("recipes");
    return q
      ? recipes.filter((r) =>
        r.service_name?.toLowerCase().includes(q) ||
        r.category?.toLowerCase().includes(q) ||
        (r.ingredients || []).some((ing) => (ing.sku_name || "").toLowerCase().includes(q))
      )
      : recipes;
  })();
  const filteredDrafts = (() => {
    const q = qFor("workbench");
    return q
      ? drafts.filter((d) =>
        d.service_name?.toLowerCase().includes(q) ||
        d.category?.toLowerCase().includes(q) ||
        (d.invoice_number || "").toLowerCase().includes(q)
      )
      : drafts;
  })();
  const filteredCommitted = (() => {
    const q = qFor("history");
    return q
      ? committed.filter((c) => {
        const pos = c.pos || {};
        return (
          (c.service_name || "").toLowerCase().includes(q) ||
          (c.date || "").toLowerCase().includes(q) ||
          (pos.invoice_number || c.invoice_number || "").toLowerCase().includes(q) ||
          (pos.client || "").toLowerCase().includes(q)
        );
      })
      : committed;
  })();
  return (
    <div className="max-w-[96%] mx-auto p-4 sm:p-6 space-y-8 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="lss-overline">COGS & Formulas</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-3 mt-1">
            <DollarSign className="w-8 h-8 text-slate-900" /> Service Recipes & COGS Analysis
          </h1>
          <p className="text-slate-600 font-medium text-sm mt-1">
            Raw material consumption formulas per service & gross margin tracking
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            onClick={async () => {
              try {
                const res = await api.get("/cogs/recipes/export", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url;
                a.download = `Geetanjali_Recipes_${todayIST()}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success("Recipes exported to Excel!");
              } catch (e) {
                toast.error(errMsg(e));
              }
            }}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-2.5 rounded-lg transition shadow-sm text-sm"
          >
            <Download className="w-4 h-4" /> Export Excel
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-slate-900 text-white font-bold px-5 py-2.5 rounded-lg hover:bg-slate-800 transition shadow-sm text-sm"
          >
            <Plus className="w-4 h-4" /> Create Service Recipe
          </button>
        </div>
      </div>

      {/* Section Navigation Tabs */}
      <div className="flex gap-2 overflow-x-auto pt-2 border-t border-slate-200">
        {[
          { id: "dashboard", label: "COGS Dashboard", icon: Gauge },
          { id: "comparison", label: "Comparison", icon: BarChart3 },
          { id: "recipes", label: "Formula Recipes", icon: Boxes },
          { id: "services", label: "POS Services", icon: ClipboardList },
          { id: "workbench", label: "Consumption Workbench", icon: ClipboardList, badge: drafts.length },
          { id: "history", label: "Committed History", icon: History, badge: committed.length },
          { id: "analysis", label: "COGS Analysis", icon: BarChart3 },
          // Every section is visible again on owner instruction (17 Oct 2026);
          // each block below is intact and fetched on switch, nothing removed.
        ].map((sec) => {
          const Icon = sec.icon;
          const isActive = activeSection === sec.id;
          return (
            <button
              key={sec.id}
              onClick={() => setActiveSection(sec.id)}
              className={`relative px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all flex items-center gap-2 shrink-0 ${
                isActive
                  ? "bg-slate-950 text-amber-300 shadow-md shadow-slate-950/20"
                  : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? "text-amber-400" : "text-slate-500"}`} />
              <span>{sec.label}</span>
              {sec.badge !== undefined && sec.badge > 0 && (
                <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${isActive ? "bg-amber-400 text-slate-950" : "bg-slate-900 text-amber-300"}`}>
                  {sec.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Recipe List */}
      {activeSection === "recipes" && (
      <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[220px] [&>div]:!mb-0">
          <SectionSearch value={searches.recipes} onChange={(v) => setSearch("recipes", v)} placeholder="Search recipes by name or ingredient..." />
        </div>
        <button onClick={() => setShowPoolModal(true)} className="flex items-center gap-2 bg-white text-slate-800 border border-slate-300 font-bold px-4 py-2.5 rounded-xl hover:bg-slate-100 text-xs shrink-0">
          <Plus className="w-4 h-4" /> New Pool
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredRecipes.map((r) => (
          <div key={r.id} className="lss-card p-6 space-y-3">
            <div className="flex justify-between items-start">
              <div>
                <span className="lss-badge bg-amber-100 text-amber-900 border border-amber-300 font-bold mb-1">{r.category || "Service"}</span>
                <h3 className="text-xl font-extrabold text-slate-900 mt-1">{r.service_name}</h3>
              </div>
              <div className="text-right">
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wider block">Material Cost</span>
                <div className="text-xl font-extrabold text-slate-900 tabular">₹{r.total_material_cost?.toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => openEdit(r)}
                className="flex items-center gap-1.5 bg-slate-900 text-white font-bold text-xs px-3 py-1.5 rounded-lg hover:bg-slate-800 transition"
              >
                <Edit className="w-3.5 h-3.5" /> Edit Recipe
              </button>
              <button
                onClick={() => handleDeleteRecipe(r)}
                className="flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 font-bold text-xs px-3 py-1.5 rounded-lg hover:bg-red-100 transition"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
            <div className="border-t border-slate-200 pt-3">
              <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Ingredients Required <span className="normal-case font-semibold text-slate-400">(rate = bottle ₹ ÷ pack qty)</span></div>
              <div className="space-y-1.5">
                {r.ingredients?.map((ing, idx) => {
                  const sku = skus.find((s) => s.id === ing.sku_id);
                  const rate = Number(ing.cost_per_unit ?? (sku ? rateFor(sku, ing.unit) : 0)) || 0;
                  const lineCost = rate * Number(ing.quantity_per_service || 0);
                  const bottle = sku ? bottlePriceOf(sku) : 0;
                  const pack = sku ? packSizeOf(sku) : 0;
                  return (
                    <div key={idx} className="flex justify-between gap-2 text-xs font-semibold text-slate-800">
                      <span>• {ing.sku_name || ing.sku_id}</span>
                      <span className="tabular font-mono text-slate-600 text-right">
                        {ing.quantity_per_service} {ing.unit} × ₹{rate.toFixed(2)} = ₹{lineCost.toFixed(2)}
                        {bottle > 0 && pack > 0 && !isWholeUnit(ing.unit) && (
                          <span className="block text-[10px] text-slate-400 font-semibold">(₹{bottle.toLocaleString()} ÷ {pack})</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
        {filteredRecipes.length === 0 && (
          <div className="md:col-span-2 bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center">
            <p className="text-slate-500 font-semibold text-sm">
              {searches.recipes ? `No recipes match "${searches.recipes}".` : "No service recipes created yet."}
            </p>
          </div>
        )}
      </div>
      </>
      )}

      {/* Tab 1: Executive COGS Dashboard — measured on the SAME Comparison window
          (service-consumption actuals vs budgets over the picked months). */}
      {activeSection === "dashboard" && (
      <>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <MonthRangePicker from={rangeFrom} to={rangeTo} allMonths={allMonths}
          onChange={(f, t) => { setRangeFrom(f); setRangeTo(t); }} />
        <span className="text-xs font-semibold text-slate-500 ml-1">
          {cmpData?.period ? `Measuring ${cmpData.period.start} → ${cmpData.period.end} · ${Number(cmpData.period.pos_services || 0).toLocaleString()} POS services` : "loading..."}
        </span>
      </div>
      {!cmpData ? (
        <div className="h-32 bg-slate-200 rounded-xl animate-pulse" />
      ) : (
      <>
      {(() => {
        const rows = cmpData.rows || [];
        const totB = rows.reduce((s, r) => s + Number(r.total_bc || 0), 0);
        const totA = rows.reduce((s, r) => s + Number(r.total_ac || 0), 0);
        const totBB = rows.reduce((s, r) => s + Number(r.total_b_base || 0), 0);
        const totAV = rows.reduce((s, r) => s + Number(r.total_av || 0), 0);
        const overall = adherence(totBB, totAV);
        const status = adhStatus(overall);
        const mKeys = cmpData.months || [];
        const mCost = mKeys.map((m) => ({
          ...m,
          bc: rows.reduce((s, r) => s + Number(((r.months || {})[m.key] || {}).bc || 0), 0),
          ac: rows.reduce((s, r) => s + Number(((r.months || {})[m.key] || {}).ac || 0), 0),
        }));
        const maxC = Math.max(1, ...mCost.flatMap((m) => [m.bc, m.ac]));
        const topCost = [...rows].filter((r) => Number(r.total_ac) > 0)
          .sort((a, b) => b.total_ac - a.total_ac).slice(0, 8);
        const maxT = Math.max(1, ...topCost.map((r) => Number(r.total_ac)));
        const leaks = [...rows].filter((r) => r.status === "leak")
          .sort((a, b) => Number(a.worst_eff ?? a.total_eff) - Number(b.worst_eff ?? b.total_eff)).slice(0, 8);
        const nGood = rows.filter((r) => r.status === "good").length;
        const nWatch = rows.filter((r) => r.status === "watch").length;
        const nLeak = rows.filter((r) => r.status === "leak").length;
        const nAll = Math.max(1, nGood + nWatch + nLeak);
        return (<>
      {/* Stock History Inventory Valuation & COGS (calculated dynamically from Stock History for selected range) */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-black uppercase tracking-wider text-slate-800">
              Inventory Valuation &amp; Stock COGS
            </span>
            <span className="text-[10px] font-black text-amber-900 bg-amber-100/90 px-2 py-0.5 rounded-full border border-amber-300">
              {monthLabel(rangeFrom)} → {monthLabel(rangeTo)}
            </span>
          </div>
          <span className="text-[11px] font-semibold text-slate-500">
            Calculated directly from Stock History · {stockHistoryCogs?.skus_count || 456} SKUs
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lss-card p-4 bg-gradient-to-br from-white to-slate-50 border-slate-200">
            <span className="lss-overline block text-slate-500 flex items-center justify-between">
              <span>Opening Stock</span>
              <span className="text-[9px] font-extrabold text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">Stock History</span>
            </span>
            <span className="text-2xl font-black text-slate-950 tabular block mt-1">
              ₹{Number(stockHistoryCogs?.opening || 0).toLocaleString()}
            </span>
            <span className="block text-[10px] font-semibold text-slate-400 mt-1">
              As of {stockHistoryCogs?.startDate || `${rangeFrom}-01`}
            </span>
          </div>
          <div className="lss-card p-4 bg-gradient-to-br from-white to-emerald-50/40 border-emerald-200">
            <span className="lss-overline block text-emerald-800">Stock In / Purchases</span>
            <span className="text-2xl font-black text-emerald-900 tabular block mt-1">
              +₹{Number(stockHistoryCogs?.inward || 0).toLocaleString()}
            </span>
            <span className="block text-[10px] font-semibold text-slate-400 mt-1">
              Inward stock received in range{stockHistoryCogs?.pi_inward ? ` · PI Invoices: ₹${Number(stockHistoryCogs.pi_inward).toLocaleString()}` : ""}
            </span>
          </div>
          <div className="lss-card p-4 bg-gradient-to-br from-white to-amber-50/40 border-amber-200">
            <span className="lss-overline block text-amber-800 flex items-center justify-between">
              <span>Closing Stock</span>
              <span className="text-[9px] font-extrabold text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">Stock History</span>
            </span>
            <span className="text-2xl font-black text-slate-950 tabular block mt-1">
              ₹{Number(stockHistoryCogs?.closing || 0).toLocaleString()}
            </span>
            <span className="block text-[10px] font-semibold text-slate-400 mt-1">
              As of {stockHistoryCogs?.endDate || rangeTo}
            </span>
          </div>
          <div className="lss-card p-4 bg-gradient-to-br from-slate-900 via-slate-800 to-amber-950 text-white border-slate-700 shadow-xs">
            <span className="lss-overline block text-amber-300 flex items-center justify-between">
              <span>Stock COGS (Consumption)</span>
              <span className="text-[9px] font-extrabold text-emerald-300 bg-emerald-950/80 px-1.5 py-0.5 rounded border border-emerald-700">Tied Out</span>
            </span>
            <span className="text-2xl font-black text-white tabular block mt-1">
              ₹{Number(stockHistoryCogs?.cogs || 0).toLocaleString()}
            </span>
            <span className="block text-[10px] font-semibold text-amber-200/90 mt-1">
              Opening + In − Closing = ₹{Number(stockHistoryCogs?.cogs || 0).toLocaleString()}
              {stockHistoryCogs?.consumption ? ` · Logged usage: ₹${Number(stockHistoryCogs.consumption).toLocaleString()}` : ""}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="lss-card p-5">
          <span className="lss-overline block text-slate-500">Overall Adherence</span>
          <span className={`text-3xl font-black tabular ${status === "good" ? "text-emerald-700" : status === "watch" ? "text-amber-600" : "text-rose-700"}`}>{overall}%</span>
          <span className="block text-[11px] font-bold text-slate-500 mt-1 uppercase">{status === "good" ? "Good adherence" : status === "watch" ? "Moderate overuse" : "High leakage"}</span>
        </div>
        <div className="lss-card p-5">
          <span className="lss-overline block text-slate-500">Budgeted COGS</span>
          <span className="text-3xl font-black text-slate-950 tabular">₹{Math.round(totB).toLocaleString()}</span>
        </div>
        <div className="lss-card p-5">
          <span className="lss-overline block text-slate-500">Actual COGS (service consumption)</span>
          <span className="text-3xl font-black text-slate-950 tabular">₹{Math.round(totA).toLocaleString()}</span>
          <span className="block text-[10px] font-semibold text-slate-400 mt-1">Technical service consumption · Matches Sales Analytics</span>
        </div>
        <div className="lss-card p-5">
          <span className="lss-overline block text-slate-500">Net Loss</span>
          <span className={`text-3xl font-black tabular ${totA - totB > 0 ? "text-rose-700" : "text-emerald-700"}`}>₹{Math.round(totA - totB).toLocaleString()}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="lss-card p-5">
          <h3 className="font-bold text-slate-950 mb-1">Budget vs actual cost by month</h3>
          <p className="text-[11px] text-slate-500 font-semibold mb-3">₹ budgeted (indigo) against ₹ service consumption (emerald)</p>
          <div className="space-y-3">
            {mCost.map((m) => (
              <div key={m.key} className="text-xs font-semibold">
                <div className="flex justify-between text-slate-700 mb-1">
                  <span className="font-extrabold">{m.label}</span>
                  <span className="tabular">₹{Math.round(m.bc).toLocaleString()} / ₹{Math.round(m.ac).toLocaleString()}</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden mb-1">
                  <div className="h-full bg-indigo-500" style={{ width: `${Math.min(100, (m.bc / maxC) * 100)}%` }} />
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (m.ac / maxC) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="lss-card p-5">
          <h3 className="font-bold text-slate-950 mb-1">Top products by actual cost</h3>
          <p className="text-[11px] text-slate-500 font-semibold mb-3">Where the service-consumption rupees went</p>
          <div className="space-y-2">
            {topCost.map((r, i) => (
              <div key={i} className="flex items-center gap-3 text-xs font-semibold">
                <span className="flex-1 truncate text-slate-800">{r.name}</span>
                <div className="w-40 h-2.5 bg-slate-200 rounded-full overflow-hidden shrink-0">
                  <div className="h-full bg-slate-800" style={{ width: `${Math.min(100, (Number(r.total_ac) / maxT) * 100)}%` }} />
                </div>
                <span className="tabular font-extrabold text-slate-900 w-20 text-right">₹{Math.round(Number(r.total_ac)).toLocaleString()}</span>
              </div>
            ))}
            {topCost.length === 0 && (
              <p className="text-xs text-slate-500 font-semibold">Nothing service-consumed in this window.</p>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="lss-card p-5">
          <h3 className="font-bold text-slate-950 mb-3">Worst efficiencies (needs attention)</h3>
          <div className="space-y-2">
            {leaks.map((r, i) => (
              <div key={i} className="flex items-center gap-3 text-xs font-semibold">
                <span className="flex-1 truncate text-slate-800">{r.name}</span>
                <div className="w-40 h-2.5 bg-slate-200 rounded-full overflow-hidden shrink-0">
                  <div className="h-full bg-rose-500" style={{ width: `${Math.min(100, Number(r.total_eff))}%` }} />
                </div>
                <span className="tabular font-extrabold text-rose-700 w-14 text-right">{r.total_eff}%</span>
              </div>
            ))}
            {leaks.length === 0 && (
              <p className="text-xs text-slate-500 font-semibold">No red zones — everything at or above 80%.</p>
            )}
          </div>
        </div>
        <div className="lss-card p-5">
          <h3 className="font-bold text-slate-950 mb-3">Product status mix</h3>
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 rounded-full shrink-0" style={{ background: `conic-gradient(#059669 0 ${(nGood / nAll) * 100}%, #d97706 ${(nGood / nAll) * 100}% ${((nGood + nWatch) / nAll) * 100}%, #e11d48 ${((nGood + nWatch) / nAll) * 100}% 100%)` }} />
            <div className="space-y-1.5 text-xs font-bold">
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-emerald-600 inline-block" /> On plan (≥90%) <span className="tabular text-slate-900">{nGood}</span></div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-amber-600 inline-block" /> Watch (80–90%) <span className="tabular text-slate-900">{nWatch}</span></div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-rose-600 inline-block" /> Leak (&lt;80%) <span className="tabular text-slate-900">{nLeak}</span></div>
            </div>
          </div>
        </div>
      </div>
        </>);
      })()}
      </>
      )}
      </>
      )}

      {/* Tab 2: Product & Category Leakage */}
      {/* Comparison: month-wise budget vs actual per product */}
      {activeSection === "comparison" && (
      <>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <MonthRangePicker from={rangeFrom} to={rangeTo} allMonths={allMonths}
          onChange={(f, t) => { setRangeFrom(f); setRangeTo(t); }} />
      </div>
      <SectionSearch value={searches.comparison} onChange={(v) => setSearch("comparison", v)} placeholder="Search comparison by product..." />
      <datalist id="cogs-pos-services">
        {(cmpData?.pos_service_names || []).map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      {!cmpData ? (
        <div className="h-32 bg-slate-200 rounded-xl animate-pulse" />
      ) : (
      <div className="lss-card p-5">
        <h3 className="font-bold text-slate-950 mb-1">Budgeted Consumption vs Actual — Month Wise</h3>
        <p className="text-[11px] font-semibold text-slate-500 mb-2">
          Service/technical consumption only. Paper bags, tissues, coffee, toilet rolls and other non-service issues are excluded here and remain visible in Stock Movement.
        </p>
        {cmpData.period && (
          <p className="text-[11px] font-semibold text-slate-500 mb-3">
            Measuring {cmpData.period.start} → {windowEnd}
            {cmpData.period.audit_date
              ? `, closed by the audit of ${cmpData.period.audit_date}`
              : " · no audit held yet, so Average stays blank until one is"}
            {" · "}{Number(cmpData.period.pos_services || 0).toLocaleString()} POS services in period
          </p>
        )}
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="lss-table">
            <thead>
              <tr>
                <th className="sticky left-0 bg-slate-950">Product</th>
                <th className="text-left border-l border-slate-700" title="Which POS service this product is used in — typed by hand, display only, never calculated">
                  POS Service
                  <span className="block text-[9px] font-semibold opacity-70">manual map</span>
                </th>
                <th className="text-right border-l border-slate-700" title="Budgeted consumption per month — the figure typed into the editor, never the sum across the range">
                  Budget Qty
                  <span className="block text-[9px] font-semibold opacity-70">per month</span>
                </th>
                {(cmpData.months || []).map((m) => (
                  <th key={m.key} className="text-center border-l border-slate-700">
                    {m.label}
                    <span className="block text-[9px] font-semibold opacity-70">budget · actual</span>
                  </th>
                ))}
                <th className="text-right border-l border-slate-700" title="Service/technical units issued out across the whole month range">Out</th>
                <th className="text-right border-l border-slate-700" title="Rupee value of the service/technical units issued out in this window — stock out × unit price, the same basis as Inventory valuation">Valuation
                  <span className="block text-[9px] font-semibold opacity-70">out × unit price</span>
                </th>
                <th className="text-right border-l border-slate-700" title="Actual ÷ POS services">Avg/Svc</th>
                <th className="text-right border-l border-slate-700" title="Budgeted ÷ actual consumption across the whole range. The badge on the product name follows the WORST month in the range — one month over budget flags the row.">Total Eff
                  <span className="block text-[9px] font-semibold opacity-70">badge = worst month</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(cmpData.rows || []).filter((r) => {
                const qq = qFor("comparison");
                return !qq || (r.name || "").toLowerCase().includes(qq);
              }).map((r, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="text-xs font-bold text-slate-900 sticky left-0 bg-white">
                    {r.name} <span className="text-slate-400 font-semibold">· {r.unit}</span>
                    <span className={`ml-1 lss-badge text-[9px] border font-bold uppercase ${r.status === "good" ? "bg-emerald-100 text-emerald-900 border-emerald-300" : r.status === "watch" ? "bg-amber-100 text-amber-950 border-amber-300" : "bg-rose-100 text-rose-900 border-rose-300"}`}>{r.status}</span>
                    {r.pack_missing ? (
                      <span
                        title={`This product was issued out but its pack size (qty) is blank in inventory, so the used quantity cannot be converted to ${r.unit || "units"}. Add the qty (pcs/ml per pack) in Inventory to fix the comparison.`}
                        className="ml-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase bg-orange-100 text-orange-800 border border-orange-400 rounded px-1 py-0.5 cursor-help"
                      >
                        ⚠ add qty
                      </span>
                    ) : null}
                  </td>
                  <td className="text-left border-l border-slate-200 px-2">
                    {r.kind === "sku" ? (
                      <PosServiceMapCell
                        rowKey={r.key}
                        selected={r.mapped_services || []}
                        options={cmpData.pos_service_names || []}
                        onSave={async (names) => {
                          try {
                            await api.post("/cogs/pos-service-map", { sku_id: r.key, pos_service_names: names });
                            fetchCmp();
                          } catch (err) {
                            toast.error(errMsg(err));
                            fetchCmp();
                          }
                        }}
                      />
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="text-right tabular text-xs font-extrabold border-l border-slate-200">
                    <div className="flex items-center justify-end gap-1.5">
                      <span>{perMonthBudget(r).toLocaleString()} <span className="text-slate-400 font-semibold">{r.budget_unit || r.unit || "unit"}</span></span>
                      <button
                        type="button"
                        onClick={() => openBudgetEditor(r)}
                        disabled={r.kind !== "sku"}
                        title={r.kind === "sku" ? "Set this product's budget" : "Category pools keep their recipe budget"}
                        aria-label={`Edit budget for ${r.name}`}
                        className="p-1 rounded-md border border-slate-300 text-slate-500 hover:border-amber-500 hover:text-amber-700 hover:bg-amber-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="block text-[9px] font-semibold text-slate-400">
                      {r.budget_flat != null ? "your budget" : "recipe default"}
                    </span>
                  </td>
                  {(cmpData.months || []).map((m) => {
                    const c = (r.months || {})[m.key] || {};
                    return (
                      <td key={m.key} className="text-right tabular text-xs font-bold border-l border-slate-200">
                        <span className={`block text-[11px] font-bold ${c.bset ? "text-indigo-700" : "text-slate-600"}`}>
                          {Number(c.bv || 0).toLocaleString()} <span className="text-slate-400 font-semibold">bud</span>
                        </span>
                        <span className="block text-[11px] text-slate-800">{Number(c.av || 0).toLocaleString()} <span className="text-slate-400">act</span></span>
                        <span className={`block text-[10px] font-extrabold ${Number(c.eff || 0) >= 100 ? "text-emerald-700" : Number(c.eff || 0) >= 90 ? "text-amber-600" : "text-rose-600"}`}>{c.eff ?? 100}%</span>
                      </td>
                    );
                  })}
                  <td className="text-right tabular text-xs font-bold border-l border-slate-200">
                    {Number(r.stock_out || 0).toLocaleString()} <span className="text-slate-400 font-semibold">{r.out_unit || r.unit || "u"}</span>
                  </td>
                  <td className="text-right tabular text-xs font-extrabold text-slate-900 border-l border-slate-200">
                    {money(r.valuation)}
                  </td>
                  <td className="text-right tabular text-xs font-bold border-l border-slate-200">
                    {r.avg_consumption == null ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <>{Number(r.avg_consumption).toLocaleString()} <span className="text-slate-400 font-semibold">{baseUnit(r.unit)}</span></>
                    )}
                    <span className="block text-[9px] text-slate-400 font-semibold">
                      {r.pos_services
                        ? `${Number(r.pos_services).toLocaleString()} svc${r.service_basis === "all" ? " (all)" : ""}`
                        : "no services"}
                    </span>
                  </td>
                  <td className="text-right tabular font-extrabold text-sm border-l border-slate-200">
                    {r.total_eff}%
                    {r.status !== "good" && (
                      <span className="block text-[9px] font-bold text-rose-600">
                        worst month {r.worst_eff ?? r.total_eff}%
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {(cmpData.rows || []).length === 0 && (
                <tr><td colSpan={99} className="text-center py-6 text-slate-500 text-xs font-semibold">Nothing has been service-consumed in this window yet — Technical products sent out from Daily Stock Out land here automatically.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
      </>
      )}

      {/* POS Services with their recipes */}
      {activeSection === "services" && (
      <>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <MonthRangePicker from={rangeFrom} to={rangeTo} allMonths={allMonths}
          onChange={(f, t) => { setRangeFrom(f); setRangeTo(t); }} />
      </div>
      <SectionSearch value={searches.services} onChange={(v) => setSearch("services", v)} placeholder="Search POS services..." />
      {!svcData ? (
        <div className="h-32 bg-slate-200 rounded-xl animate-pulse" />
      ) : (
      <div className="lss-card p-5">
        <h3 className="font-bold text-slate-950 mb-1">POS Services & Linked Recipes</h3>
        <p className="text-xs text-slate-500 font-medium mb-3">
          Every billed service from {monthLabel(rangeFrom)} to {monthLabel(rangeTo)}, its matched recipe,
          billed count and standard cost — the same window Comparison measures Actual over.
        </p>
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="lss-table">
            <thead>
              <tr>
                <th>POS Service</th>
                <th>Linked Recipe</th>
                <th className="text-right">Billed Count</th>
                <th className="text-right">Avg Price</th>
                <th className="text-right">Std Recipe Cost</th>
              </tr>
            </thead>
            <tbody>
              {(svcData?.services || []).filter((sv) => {
                const qq = qFor("services");
                return !qq || (sv.service || "").toLowerCase().includes(qq);
              }).map((sv, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="font-bold text-slate-900 text-xs">{sv.service}</td>
                  <td className="text-xs text-slate-700">{sv.recipe ? <span className="font-semibold text-emerald-700">{sv.recipe}</span> : <span className="italic text-amber-700">No recipe — create one</span>}</td>
                  <td className="text-right tabular text-xs">{sv.count}</td>
                  <td className="text-right tabular text-xs">₹{Number(sv.avg_price).toLocaleString()}</td>
                  <td className="text-right tabular text-xs font-bold">₹{Number(sv.std_cost).toLocaleString()}</td>
                </tr>
              ))}
              {(svcData?.services || []).length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-slate-500 text-xs font-semibold">No billed services in this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
      </>
      )}

      {/* POS Daily Consumption Draft Workbench */}
      {activeSection === "workbench" && (
      <>
      <SectionSearch value={searches.workbench} onChange={(v) => setSearch("workbench", v)} placeholder="Search pending drafts by service or invoice..." />
      <div className="lss-card p-6 bg-slate-50 border border-slate-300">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
              <Layers className="w-5 h-5 text-amber-600" /> Daily POS Consumption Workbench
            </h2>
            <p className="text-slate-600 text-xs font-semibold mt-0.5">
              Review EOD POS imported services and select the products each service used — consumption comes off the single stock.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              onClick={() => setActiveSection("history")}
              className="flex items-center gap-2 bg-indigo-700 text-white font-bold px-4 py-2 rounded-lg hover:bg-indigo-800 transition text-xs"
            >
              <Eye className="w-4 h-4" /> View Committed Consumption ({committed.length})
            </button>
          </div>
        </div>

        {drafts.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center">
            <p className="text-slate-500 font-semibold text-sm">
              No pending consumption drafts. Upload a POS EOD file to auto-generate service consumption drafts from your recipes.
            </p>
          </div>
        ) : filteredDrafts.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center">
            <p className="text-slate-500 font-semibold text-sm">No pending drafts match "{searches.workbench}".</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDrafts.map((d) => (
              <div key={d.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="lss-badge bg-indigo-50 text-indigo-700 border border-indigo-200 font-bold">{d.category || "Service"}</span>
                    <span className="text-xs font-mono font-bold text-slate-600">Invoice #{d.invoice_number || "—"}</span>
                    {d.needs_manual_product && (
                      <span className="lss-badge bg-amber-50 text-amber-700 border border-amber-300 font-bold">Needs Manual Product</span>
                    )}
                  </div>
                  <h4 className="font-extrabold text-slate-900 text-base">{d.service_name}</h4>
                  <div className="text-xs text-slate-700 font-semibold flex items-center gap-3">
                    <span>Date: {d.date || "—"}</span>
                    <span>•</span>
                    <span>Ingredients: {d.ingredients?.length || 0} items</span>
                    <span>•</span>
                    <span className={d.recipe_matched ? "text-emerald-700" : "text-amber-700"}>{d.recipe_matched ? "Recipe matched" : "No recipe match"}</span>
                  </div>
                </div>
                <button
                  onClick={() => openCommitModal(d)}
                  className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs px-4 py-2 rounded-lg transition shrink-0"
                >
                  <Trash2 className="w-4 h-4 hidden" /> Commit Consumption
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </>
      )}

      {/* Committed Consumption History */}
      {activeSection === "history" && (
      <>
      <SectionSearch value={searches.history} onChange={(v) => setSearch("history", v)} placeholder="Search committed services by name, invoice or client..." />
      <div className="lss-card p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600" /> Committed Consumption History
            </h2>
            <p className="text-slate-600 text-xs font-semibold mt-0.5">
              All committed services with POS & service details, invoice, client and material cost.
            </p>
          </div>
          <span className="lss-badge bg-indigo-50 text-indigo-700 border border-indigo-200 font-bold">{committed.length} committed</span>
        </div>
        {committed.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center">
            <p className="text-slate-500 font-semibold text-sm">
              No committed consumption yet. Commit pending drafts from the Consumption Workbench tab.
            </p>
          </div>
        ) : filteredCommitted.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center">
            <p className="text-slate-500 font-semibold text-sm">No committed services match "{searches.history}".</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCommitted.map((c, i) => {
              const pos = c.pos || {};
              const ings = c.ingredients_used || [];
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="lss-badge bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">{c.service_name}</span>
                    <span className="lss-badge bg-slate-100 text-slate-700 border border-slate-200 font-bold">Date: {c.date || "—"}</span>
                    <span className="text-xs font-mono font-bold text-slate-600">Invoice #{pos.invoice_number || c.invoice_number || "—"}</span>
                    <span className="text-xs font-semibold text-slate-700">Client: {pos.client || "—"}</span>
                    {(c.staff_name || (pos.staff || []).length > 0) && (
                      <span className="text-xs font-semibold text-indigo-700">Staff: {c.staff_name || (pos.staff || []).map((s) => s.name).join(", ")}</span>
                    )}
                    <span className="text-xs font-semibold text-emerald-700 tabular">Service Price: ₹{Number(pos.net_price ?? c.revenue_generated ?? 0).toLocaleString()}</span>
                    <span className="ml-auto text-sm font-extrabold text-slate-900 tabular">Material Cost: ₹{Number(c.total_cost || 0).toLocaleString()}</span>
                  </div>
                  <details className="text-sm text-slate-700">
                    <summary className="cursor-pointer text-xs font-bold text-slate-600 hover:text-slate-900">Ingredient breakdown ({ings.length})</summary>
                    <div className="mt-2 space-y-1">
                      {ings.map((ing, j) => (
                        <div key={j} className="flex justify-between text-xs font-semibold text-slate-800 border-b border-slate-100 pb-1">
                          <span>• {ing.sku_name || ing.sku_id}</span>
                          <span className="tabular font-mono text-slate-600">{ing.qty} {ing.unit} (₹{Number(ing.cost || 0).toLocaleString()})</span>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </>
      )}

      {/* Gross Margin & Consumption Analysis — built on the live flows: billed
          POS services with recipe-standard material cost, plus the
          service-consumption actuals the Comparison measures. The old
          service-consumption log table is gone because that log was never
          written to (owner, Oct 2026). */}
      {activeSection === "analysis" && (
      <>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <MonthRangePicker from={rangeFrom} to={rangeTo} allMonths={allMonths}
          onChange={(f, t) => { setRangeFrom(f); setRangeTo(t); }} />
      </div>
      <SectionSearch value={searches.analysis} onChange={(v) => setSearch("analysis", v)} placeholder="Search services or products..." />
      {(!svcData && !cmpData) ? (
        <div className="h-32 bg-slate-200 rounded-xl animate-pulse" />
      ) : (
      <>
      {(() => {
        const qA = qFor("analysis");
        const svcRows = (svcData?.services || []).filter((sv) => !qA || (sv.service || "").toLowerCase().includes(qA));
        const totRev = svcRows.reduce((s, sv) => s + Number(sv.revenue || 0), 0);
        const totStd = svcRows.reduce((s, sv) => s + Number(sv.std_cost || 0), 0);
        const topRev = [...svcRows].sort((a, b) => Number(b.revenue) - Number(a.revenue)).slice(0, 8);
        const maxR = Math.max(1, ...topRev.map((sv) => Number(sv.revenue)));
        const cmpRows = (cmpData?.rows || []).filter((r) =>
          (Number(r.stock_out) > 0 || Number(r.total_av) > 0) &&
          (!qA || (r.name || "").toLowerCase().includes(qA)));
        return (<>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="lss-card p-5">
            <span className="lss-overline block text-slate-500">Service Revenue</span>
            <span className="text-3xl font-black text-slate-950 tabular">₹{Math.round(totRev).toLocaleString()}</span>
          </div>
          <div className="lss-card p-5">
            <span className="lss-overline block text-slate-500">Std Material (recipes)</span>
            <span className="text-3xl font-black text-slate-950 tabular">₹{Math.round(totStd).toLocaleString()}</span>
          </div>
          <div className="lss-card p-5">
            <span className="lss-overline block text-slate-500">Gross Margin</span>
            <span className={`text-3xl font-black tabular ${totRev - totStd >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{totRev > 0 ? Math.round(((totRev - totStd) / totRev) * 1000) / 10 : 0}%</span>
          </div>
        </div>
        <div className="lss-card p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-600" /> Service-wise Material Margin
          </h2>
          <p className="text-[11px] text-slate-500 font-semibold mb-4">Billed count × recipe-standard cost vs billed revenue, {svcData ? `${svcData.start_date} → ${svcData.end_date}` : "selected range"}. Services without a recipe carry no material cost until one is created.</p>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="lss-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th className="text-right">Billed</th>
                  <th className="text-right">Revenue</th>
                  <th className="text-right">Std Material</th>
                  <th className="text-right">Margin ₹</th>
                  <th className="text-right">Margin %</th>
                </tr>
              </thead>
              <tbody>
                {svcRows.map((sv, i) => {
                  const rev = Number(sv.revenue || 0), std = Number(sv.std_cost || 0);
                  const mg = rev - std, pct = rev > 0 ? Math.round((mg / rev) * 1000) / 10 : 0;
                  return (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="font-bold text-slate-900 text-xs">{sv.service} {!sv.recipe && <span className="italic font-semibold text-amber-700">· no recipe</span>}</td>
                      <td className="text-right tabular text-xs">{sv.count}</td>
                      <td className="text-emerald-700 font-bold tabular text-xs">₹{Math.round(rev).toLocaleString()}</td>
                      <td className="text-rose-700 font-bold tabular text-xs">₹{Math.round(std).toLocaleString()}</td>
                      <td className={`text-right tabular text-xs font-bold ${mg >= 0 ? "text-slate-900" : "text-rose-700"}`}>₹{Math.round(mg).toLocaleString()}</td>
                      <td className={`text-right tabular text-xs font-extrabold ${pct >= 50 ? "text-emerald-700" : pct >= 0 ? "text-amber-600" : "text-rose-700"}`}>{pct}%</td>
                    </tr>
                  );
                })}
                {svcRows.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-xs font-semibold">No billed services in this range{qA ? ` matching "${searches.analysis}"` : ""}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="lss-card p-5 mb-6">
          <h3 className="font-bold text-slate-950 mb-1">Top services by revenue</h3>
          <p className="text-[11px] text-slate-500 font-semibold mb-3">Revenue (slate) against recipe-standard material (rose)</p>
          <div className="space-y-3">
            {topRev.map((sv, i) => {
              const rev = Number(sv.revenue || 0), std = Number(sv.std_cost || 0);
              return (
                <div key={i} className="text-xs font-semibold">
                  <div className="flex justify-between text-slate-700 mb-1">
                    <span className="font-extrabold truncate mr-2">{sv.service} <span className="font-semibold text-slate-400">×{sv.count}</span></span>
                    <span className="tabular shrink-0">₹{Math.round(rev).toLocaleString()}</span>
                  </div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden mb-1">
                    <div className="h-full bg-slate-800" style={{ width: `${Math.min(100, (rev / maxR) * 100)}%` }} />
                  </div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-rose-400" style={{ width: `${Math.min(100, (std / maxR) * 100)}%` }} />
                  </div>
                </div>
              );
            })}
            {topRev.length === 0 && (
              <p className="text-xs text-slate-500 font-semibold">Nothing billed in this range.</p>
            )}
          </div>
        </div>
        <div className="lss-card p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-indigo-600" /> Product Consumption in Window
          </h2>
          <p className="text-[11px] text-slate-500 font-semibold mb-4">Service/technical stock with its converted used quantity, cost and adherence — the same rows Comparison measures. Non-service issues are excluded.</p>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="lss-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-right">Issued Out</th>
                  <th className="text-right">Used</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">Budget</th>
                  <th className="text-right">Eff %</th>
                </tr>
              </thead>
              <tbody>
                {cmpRows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="font-bold text-slate-900 text-xs">{r.name}</td>
                    <td className="text-right tabular text-xs">{Number(r.stock_out || 0).toLocaleString()} <span className="text-slate-400">{r.out_unit || r.unit}</span></td>
                    <td className="text-right tabular text-xs">{Number(r.total_av || 0).toLocaleString()} <span className="text-slate-400">{r.unit}</span></td>
                    <td className="text-rose-700 font-bold tabular text-xs">₹{Math.round(Number(r.total_ac || 0)).toLocaleString()}</td>
                    <td className="text-slate-700 tabular text-xs">{Number(r.total_b_base || 0).toLocaleString()}</td>
                    <td className={`text-right tabular text-xs font-extrabold ${Number(r.worst_eff ?? r.total_eff) >= 100 ? "text-emerald-700" : Number(r.worst_eff ?? r.total_eff) >= 90 ? "text-amber-600" : "text-rose-700"}`}>{r.total_eff}%</td>
                  </tr>
                ))}
                {cmpRows.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-xs font-semibold">Nothing service-consumed in this window{qA ? ` matching "${searches.analysis}"` : ""}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>);
      })()}
      </>
      )}
      </>
      )}

      {showPoolModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => setShowPoolModal(false)}>
          <div className="bg-white border border-slate-200 shadow-xl rounded-xl p-6 w-full sm:max-w-lg space-y-4 text-slate-900 my-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-extrabold text-slate-900">New Category Pool</h3>
            <p className="text-xs text-slate-500 font-medium">Group interchangeable products. Membership = SKU name/brand/category contains any keyword (comma-separated).</p>
            <form onSubmit={handleSavePool} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Pool Name *</label>
                <input value={poolName} onChange={(e) => setPoolName(e.target.value)} placeholder="e.g. Backbar Shampoo" className="lss-input w-full text-sm font-semibold" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase block mb-1">Keywords (comma-separated)</label>
                <input value={poolKeys} onChange={(e) => setPoolKeys(e.target.value)} placeholder="e.g. shampoo, shmp" className="lss-input w-full text-sm font-semibold" />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowPoolModal(false)} className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase">Cancel</button>
                <button type="submit" className="lss-btn-gold px-4 py-2 text-xs font-bold uppercase">Save Pool</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set the ONE budget figure for a product. Same number every month. */}
      {budgetEdit && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => setBudgetEdit(null)}>
          <div className="bg-white border border-slate-200 shadow-xl rounded-xl p-6 w-full sm:max-w-md space-y-4 text-slate-900 my-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-extrabold text-slate-900">Set Budget</h3>
            <p className="text-xs text-slate-500 font-medium">
              <span className="font-extrabold text-slate-800">{budgetEdit.name}</span> — one budget figure, used in
              every month shown. Set it once here; the rest of the Comparison row stays calculated.
            </p>
            <div>
              <label className="text-xs font-bold text-slate-700 uppercase block mb-1">
                Budget consumption per month
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="any"
                  autoFocus
                  value={budgetValue}
                  onChange={(e) => setBudgetValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveFlatBudget(); }}
                  className="lss-input w-full text-sm font-bold tabular"
                />
                <select
                  value={budgetUnit}
                  onChange={(e) => setBudgetUnit(e.target.value)}
                  aria-label="Budget unit"
                  title="The unit this budget is expressed in"
                  className="px-3 py-2 text-sm font-extrabold rounded-lg border border-slate-300 bg-white text-slate-800 focus:border-amber-500 outline-none"
                >
                  {Array.from(new Set([budgetUnit, budgetEdit.unit, ...BUDGET_UNITS]
                    .filter(Boolean))).map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
              <p className="text-[11px] text-slate-500 font-semibold mt-1">
                Pick the unit you mean — stock is planned in containers (“2 boxes”) as often as in
                millilitres. The comparison converts it to the product’s base unit before measuring
                it against actual consumption, so the percentage stays honest.
              </p>
            </div>
            {(cmpData?.months || []).length > 0 && (
              <p className="text-[11px] text-slate-500 font-semibold">
                Applies to {(cmpData.months || []).map((m) => m.label).join(", ")}.
              </p>
            )}
            <div className="flex justify-between gap-3">
              <button type="button" onClick={resetFlatBudget} disabled={savingBudget} className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase disabled:opacity-40">
                Reset to Recipe
              </button>
              <div className="flex gap-3">
                <button type="button" onClick={() => setBudgetEdit(null)} className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase">Cancel</button>
                <button type="button" onClick={saveFlatBudget} disabled={savingBudget} className="lss-btn-gold px-4 py-2 text-xs font-bold uppercase disabled:opacity-40">
                  {savingBudget ? "Saving…" : "Save Budget"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for creating a new recipe */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => { setShowModal(false); setEditing(null); }}>
          <div className="bg-white border border-slate-200 shadow-xl rounded-xl p-6 w-full sm:max-w-3xl max-h-[90vh] flex flex-col space-y-4 text-slate-900 my-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-extrabold text-slate-900 shrink-0">{editing ? `Edit Recipe: ${editing.service_name}` : "Create New Service Recipe"}</h3>
            <form onSubmit={handleSaveRecipe} className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Service Name</label>
                <input
                  type="text"
                  placeholder="e.g. Kerastase Hair Spa"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  className="lss-input w-full"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Category</label>
                <input
                  type="text"
                  placeholder="e.g. Hair Care"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="lss-input w-full"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Ingredients Required <span className="normal-case font-semibold text-slate-400">(cost = unit price ÷ pack qty × use qty — never full bottle)</span></label>
                {ingredients.map((ing, idx) => {
                  const sel = ing.mode === "pool" ? null : skus.find((s) => s.id === ing.sku_id);
                  const poolMembers = ing.mode === "pool" ? (pools.find((pp) => pp.name === ing.category_pool)?.members || []) : [];
                  const poolRates = poolMembers.map((m) => rateFor(m, ing.unit));
                  const poolRate = poolRates.length ? poolRates.reduce((a, b) => a + b, 0) / poolRates.length : 0;
                  const rate = ing.mode === "pool" ? poolRate : (sel ? rateFor(sel, ing.unit) : 0);
                  const lineCost = rate * (Number(ing.quantity_per_service) || 0);
                  const setIng = (patch) => {
                    const copy = [...ingredients];
                    copy[idx] = { ...copy[idx], ...patch };
                    setIngredients(copy);
                  };
                  return (
                  <div key={idx} className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                    <div className="flex gap-1 mb-2">
                      {[["sku", "\ud83d\udce6 Specific SKU"], ["pool", "\ud83d\udcc1 Category Pool"]].map(([m, label]) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setIng({ mode: m, sku_id: m === "sku" ? ing.sku_id : "", category_pool: m === "pool" ? ing.category_pool : "" })}
                          className={`px-2.5 py-1 text-[11px] font-extrabold uppercase rounded-lg border transition ${ing.mode === m ? "bg-slate-900 text-amber-300 border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1 min-w-0">
                        {ing.mode === "pool" ? (
                          <select
                            value={ing.category_pool || ""}
                            onChange={(e) => setIng({ category_pool: e.target.value })}
                            className="lss-input w-full text-xs font-semibold"
                          >
                            <option value="">Select pool...</option>
                            {pools.map((pp) => (
                              <option key={pp.id} value={pp.name}>{pp.name} ({pp.member_count} items)</option>
                            ))}
                          </select>
                        ) : (
                          <SearchableSelect
                            options={skus}
                            value={ing.sku_id}
                            onChange={(val) => {
                              const copy = [...ingredients];
                              copy[idx].sku_id = val;
                              setIngredients(copy);
                            }}
                            placeholder="Search SKU product name..."
                            getOptionLabel={(s) => skuRateLabel(s)}
                            getOptionValue={(s) => s.id}
                          />
                        )}
                      </div>
                    <input
                      type="number"
                      placeholder="Qty"
                      value={ing.quantity_per_service}
                      onChange={(e) => {
                        const copy = [...ingredients];
                        copy[idx].quantity_per_service = parseFloat(e.target.value);
                        setIngredients(copy);
                      }}
                      className="lss-input w-20 text-xs font-semibold"
                    />
                    <select
                      value={ing.unit}
                      onChange={(e) => {
                        const copy = [...ingredients];
                        copy[idx].unit = e.target.value;
                        setIngredients(copy);
                      }}
                      className="lss-input w-20 text-xs font-semibold"
                    >
                      <option value="ml">ml</option>
                          <option value="grm">grm</option>
                          <option value="pcs">pcs</option>
                        </select>
                      <button
                        type="button"
                        onClick={() => removeIngredientRow(idx)}
                        className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded transition self-center"
                        title="Remove ingredient"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {(sel || (ing.mode === "pool" && ing.category_pool)) && (
                      <div className="mt-1.5 text-[11px] font-semibold text-slate-600 flex flex-wrap gap-x-3 gap-y-0.5">
                        {ing.mode === "pool" ? (
                          <span className="tabular">Pool avg: <b>₹{rate.toFixed(2)}/{ing.unit}</b> × {Number(ing.quantity_per_service) || 0} = <b className="text-emerald-700">₹{lineCost.toFixed(2)}</b> <span className="text-slate-400">({poolMembers.length} members)</span></span>
                        ) : isWholeUnit(ing.unit) ? (
                          <span className="tabular">Whole unit cost: ₹{rate.toFixed(2)} × {Number(ing.quantity_per_service) || 0} = <b className="text-slate-900">₹{lineCost.toFixed(2)}</b></span>
                        ) : (
                          <span className="tabular">₹{bottlePriceOf(sel).toLocaleString()} ÷ {packSizeOf(sel) || 100} = <b>₹{rate.toFixed(2)}/{ing.unit}</b> × {Number(ing.quantity_per_service) || 0} = <b className="text-emerald-700">₹{lineCost.toFixed(2)}</b></span>
                        )}
                        {sel && (Number(sel.store_qty || 0) > 0 ? (
                          <span className="text-emerald-700 font-bold">● In stock: {Number(sel.store_qty).toLocaleString()} {sel.unit || ""}</span>
                        ) : (
                          <span className="text-rose-700 font-extrabold">● No stock left — purchase it in first</span>
                        ))}
                      </div>
                    )}
                    </div>
                  );
                })}
                  <button
                    type="button"
                    onClick={addIngredientRow}
                    className="text-xs font-bold text-amber-800 mt-2.5 flex items-center gap-1 hover:underline"
                  >
                    + Add Ingredient Line
                  </button>
                </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 shrink-0">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditing(null); }}
                  className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button type="submit" className="lss-btn-gold px-4 py-2 text-xs font-bold uppercase tracking-wider">
                  {editing ? "Update Recipe" : "Save Recipe"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Edit & Commit Consumption */}
      {commitModalDraft && (() => {
        const pos = commitModalDraft.pos || {};
        const staff = pos.staff || [];
        return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => setCommitModalDraft(null)}>
          <div className="bg-white border border-slate-200 shadow-xl rounded-xl p-6 w-full sm:max-w-4xl max-h-[90vh] flex flex-col space-y-4 text-slate-900 my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Commit Consumption</div>
                <h3 className="text-xl font-extrabold text-slate-900 mt-0.5">{commitModalDraft.service_name}</h3>
                <div className="text-xs text-slate-600 font-semibold mt-1 flex gap-3 flex-wrap">
                  <span>Invoice #{commitModalDraft.invoice_number || "—"}</span>
                  <span>•</span>
                  <span>{fmtDate(commitModalDraft.date) || "—"}</span>
                  <span>•</span>
                  <span>{fmt12(pos.time)}</span>
                </div>
              </div>
              <button onClick={() => setCommitModalDraft(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* POS transaction details */}
            {(pos.client || pos.net_price !== undefined || staff.length > 0 || pos.type) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Client</div>
                  <div className="text-sm font-extrabold text-slate-900 truncate">{pos.client || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Service Price</div>
                  <div className="text-sm font-extrabold text-emerald-700 tabular">
                    ₹{Number(pos.net_price ?? 0).toLocaleString()}
                    {pos.total_discount > 0 && (
                      <span className="text-slate-400 font-semibold text-xs ml-1">(-₹{Number(pos.total_discount || 0).toLocaleString()} disc)</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Rate / Qty</div>
                  <div className="text-sm font-extrabold text-slate-900 tabular">
                    ₹{Number(pos.rate ?? 0).toLocaleString()} × {pos.quantity ?? 1}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Staff</div>
                  <div className="text-sm font-extrabold text-slate-900 truncate">
                    {staff.length ? staff.map((s) => s.name).join(", ") : "—"}
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                  Ingredients & Quantities <span className="text-slate-400 normal-case font-semibold">(edit before committing)</span>
                </label>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={addCommitIng} className="text-xs font-bold text-amber-800 flex items-center gap-1 hover:underline">
                    <Plus className="w-3.5 h-3.5" /> Add Item
                  </button>
                </div>
              </div>

              <div className="space-y-2 overflow-y-auto flex-1 min-h-0 pr-1">
                <div className="hidden md:grid md:grid-cols-[1fr_90px_130px_170px_36px] gap-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider px-1">
                  <span>Product / SKU</span>
                  <span>Qty</span>
                  <span>Unit</span>
                  <span>Stock Source</span>
                  <span></span>
                </div>
                {commitIngs.map((ing, idx) => {
                  const sel = skus.find((x) => x.id === ing.sku_id);
                  const rate = sel ? rateFor(sel, ing.unit) : 0;
                  const lineCost = rate * (Number(ing.qty) || 0);
                  return (
                  <div key={idx} className="flex flex-col bg-slate-50 border border-slate-200 rounded-lg p-2 gap-1.5">
                    <div className="flex flex-col md:grid md:grid-cols-[1fr_90px_130px_170px_36px] gap-2 md:gap-2 items-center">
                    <div className="w-full min-w-0">
                      <SearchableSelect
                        options={skus}
                        value={ing.sku_id}
                        onChange={(val) => {
                          const s = skus.find((x) => x.id === val);
                          updateCommitIng(idx, { sku_id: val, sku_name: s?.name || "" });
                        }}
                        placeholder="Search SKU..."
                        getOptionLabel={(s) => skuRateLabel(s)}
                        getOptionValue={(s) => s.id}
                      />
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={ing.qty}
                      onChange={(e) => updateCommitIng(idx, { qty: e.target.value })}
                      className="lss-input w-full md:w-20 text-xs font-semibold"
                    />
                    <select
                      value={ing.unit}
                      onChange={(e) => updateCommitIng(idx, { unit: e.target.value })}
                      className="lss-input w-full md:w-full text-xs font-semibold"
                    >
                      <option value="ml">ml</option>
                      <option value="grm">grm</option>
                      <option value="pcs">pcs</option>
                      <option value="bottle">bottle (open whole)</option>
                    </select>
                    <button onClick={() => removeCommitIng(idx)} className="text-rose-600 hover:text-rose-800" title="Remove item">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    </div>
                    {sel && (
                      <div className="text-[11px] font-semibold text-slate-600 tabular">
                        {isWholeUnit(ing.unit)
                          ? <>Whole: ₹{rate.toFixed(2)} × {Number(ing.qty) || 0} = <b className="text-slate-900">₹{lineCost.toFixed(2)}</b></>
                          : <>₹{bottlePriceOf(sel).toLocaleString()} ÷ {packSizeOf(sel) || 100} = <b>₹{rate.toFixed(2)}/{ing.unit}</b> × {Number(ing.qty) || 0} = <b className="text-emerald-700">₹{lineCost.toFixed(2)}</b></>}
                        {Number(sel.store_qty || 0) > 0 ? (
                          <span className="ml-2 text-emerald-700 font-bold">● In stock: {Number(sel.store_qty).toLocaleString()} {sel.unit || ""}</span>
                        ) : (
                          <span className="ml-2 text-rose-700 font-extrabold">● No stock left — purchase it in first</span>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 shrink-0">
              <button onClick={() => setCommitModalDraft(null)} className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase tracking-wider">
                Cancel
              </button>
              <button onClick={handleCommit} className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-lg">
                Commit Consumption
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
